import { URLS, createJurisdictionHeaders } from '@/data';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { useUserStore } from '@/hooks/use-user-store';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export type UserInfo = z.infer<typeof UserInfoSchema>;

const UserInfoDataSchema = z.object({
    username: z.string(),
    email: z.string(),
    phone_number: z.string(),
    phone_number_country_code: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    dob: z.object({
        dob_day: z.number(),
        dob_month: z.number(),
        dob_year: z.number(),
    }),
    kyc_verified: z.boolean(),
    national_id: z.string(),
    address: z.object({
        street_name: z.string(),
        city: z.string(),
        state: z.string(),
        post_code: z.string(),
        country: z.string(),
    }),
    signup_code: z.string(),
    encryptedSSN9: z.string().optional(),
});

const UserInfoSchema = z.object({
    data: UserInfoDataSchema,
});

export const getUserInfo = async (): Promise<UserInfo> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/user-information`, {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });
    const data = await response.json();
    if (!response.ok) {
        throw data;
    }
    return UserInfoSchema.parse(data);
};

/**
 * Regarding to `refetchOnMount`, this is a temporary fix to meet the timeline for the next release (Dec. 9th).
 * In the future, we can refactor useUserAuthInfo to accept the full set of query options
 */
export const useAuthUserInfo = ({ refetchOnMount }: { refetchOnMount?: boolean | 'always' } = {}) => {
    const sessionExpired = useUserStore(state => state.actions.sessionHasExpired);
    return useQuery({
        queryKey: authUserKeys.info(),
        queryFn: () => getUserInfo(),
        enabled: !sessionExpired(),
        staleTime: 1000 * 60 * 3,
        select: data => data.data,
        refetchOnMount: refetchOnMount ?? true,
    });
};
